import { useMemo, useRef, useCallback } from "react";
import { useFullscreenSwipeDismiss } from "@/hooks/useFullscreenSwipeDismiss";
import {
  type MediaViewerGestureLock,
  isMediaViewerSwipeBlocked,
} from "@/lib/native/mediaViewerGestureLock";

type GallerySwipeOptions = {
  enabled: boolean;
  onDismiss: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  canSwipePrev?: boolean;
  canSwipeNext?: boolean;
  gestureLockRef?: React.RefObject<MediaViewerGestureLock>;
};

/** Vertical swipe down to dismiss; horizontal swipe for gallery prev/next. */
export function useGallerySwipeHandlers({
  enabled,
  onDismiss,
  onPrev,
  onNext,
  canSwipePrev = true,
  canSwipeNext = true,
  gestureLockRef,
}: GallerySwipeOptions) {
  const swipeDismiss = useFullscreenSwipeDismiss(onDismiss, { gestureLockRef });
  const start = useRef<{ x: number; y: number } | null>(null);
  const maxTouches = useRef(0);

  const resetTracking = useCallback(() => {
    start.current = null;
    maxTouches.current = 0;
  }, []);

  return useMemo(
    () =>
      enabled
        ? {
            onTouchStart: (e: React.TouchEvent) => {
              swipeDismiss.onTouchStart(e);
              maxTouches.current = Math.max(
                maxTouches.current,
                e.touches.length,
              );
              if (
                isMediaViewerSwipeBlocked(
                  gestureLockRef?.current,
                  maxTouches.current,
                )
              ) {
                start.current = null;
                return;
              }
              start.current = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
              };
            },
            onTouchMove: (e: React.TouchEvent) => {
              swipeDismiss.onTouchMove(e);
              maxTouches.current = Math.max(
                maxTouches.current,
                e.touches.length,
              );
              if (
                e.touches.length >= 2 ||
                isMediaViewerSwipeBlocked(
                  gestureLockRef?.current,
                  maxTouches.current,
                )
              ) {
                start.current = null;
              }
            },
            onTouchEnd: (e: React.TouchEvent) => {
              swipeDismiss.onTouchEnd(e);
              if (
                isMediaViewerSwipeBlocked(
                  gestureLockRef?.current,
                  maxTouches.current,
                ) ||
                !start.current
              ) {
                resetTracking();
                return;
              }
              const touch = e.changedTouches[0];
              const dx = touch.clientX - start.current.x;
              const dy = touch.clientY - start.current.y;
              const absDx = Math.abs(dx);
              const absDy = Math.abs(dy);
              if (absDx > 55 && absDx > absDy * 1.15) {
                if (dx > 0 && canSwipePrev) onPrev?.();
                else if (dx < 0 && canSwipeNext) onNext?.();
              }
              resetTracking();
            },
          }
        : {},
    [
      enabled,
      swipeDismiss,
      gestureLockRef,
      onPrev,
      onNext,
      canSwipePrev,
      canSwipeNext,
      resetTracking,
    ],
  );
}
